import {
  AiPodCanvasNode,
  AiPodConnection,
  AiPodGraph,
  AiPodNodeDefinition,
  AiPodTransitionDefinition,
} from '../../types/ai-pod-canvas';

/** Canvas nodes/connections -> persisted graph (POST/PUT payload to /ai-pods/:id/graph). */
export const buildAiPodGraph = (nodes: AiPodCanvasNode[], connections: AiPodConnection[]): AiPodGraph => {
  const nodeDefs: AiPodNodeDefinition[] = nodes.map((node) => {
    const common = {
      id: node.id,
      name: node.name,
      title: node.title ?? node.name,
      summary: node.summary,
      nodeClass: node.nodeClass,
      canvasType: node.nodeClass,
      position: node.position,
      inputs: node.inputs,
      outputs: node.outputs,
    };

    switch (node.kind) {
      case 'trigger':
      case 'conversation':
      case 'action':
        return { ...common, kind: node.kind, agentTypeId: node.data?.agentTypeId, config: node.data ?? {} };
      case 'router':
        return { ...common, kind: 'router', config: { ...(node.data ?? {}), expression: node.data?.expression } };
      case 'human-handoff':
        return {
          ...common,
          kind: 'human-handoff',
          config: { ...(node.data ?? {}), queueId: node.data?.queueId, teamId: node.data?.teamId },
        };
      case 'end':
      default:
        return { ...common, kind: 'end', config: { result: node.data?.result ?? 'success' } };
    }
  });

  const transitionDefs: AiPodTransitionDefinition[] = connections.map((connection) => ({
    id: connection.id,
    sourceNodeId: connection.sourceNodeId,
    sourcePortId: connection.sourceOutputId,
    targetNodeId: connection.targetNodeId,
    targetPortId: connection.targetInputId,
    label: connection.label?.trim() || 'Next',
    conditionType: connection.conditionType ?? 'custom',
    priority: connection.priority,
    isLoopback: connection.isLoopback,
    metadata: connection.metadata,
  }));

  return { nodes: nodeDefs, transitions: transitionDefs };
};

/** Persisted graph -> canvas nodes/connections (GET /ai-pods/:id/graph response). */
export const parseAiPodGraph = (graph: AiPodGraph): { nodes: AiPodCanvasNode[]; connections: AiPodConnection[] } => {
  const nodes: AiPodCanvasNode[] = graph.nodes.map((node) => ({
    id: node.id,
    kind: node.kind,
    name: node.name,
    title: node.title ?? node.name,
    summary: node.summary,
    nodeClass: node.kind,
    position: node.position,
    inputs: node.inputs,
    outputs: node.outputs,
    data:
      node.kind === 'trigger' || node.kind === 'conversation' || node.kind === 'action'
        ? { nodeClass: node.kind, agentTypeId: node.agentTypeId, ...node.config }
        : node.kind === 'router'
        ? { nodeClass: 'router', expression: (node.config as any)?.expression ?? '', ...node.config }
        : node.kind === 'human-handoff'
        ? { nodeClass: 'human-handoff', ...node.config }
        : { nodeClass: 'end', result: (node.config as any)?.result ?? 'success' },
  }));

  const connections: AiPodConnection[] = graph.transitions.map((t) => ({
    id: t.id,
    sourceNodeId: t.sourceNodeId,
    sourceOutputId: t.sourcePortId,
    targetNodeId: t.targetNodeId,
    targetInputId: t.targetPortId,
    label: t.label,
    conditionType: t.conditionType,
    priority: t.priority,
    isLoopback: t.isLoopback,
    metadata: t.metadata,
  }));

  return { nodes, connections };
};
